import { isEqual } from "lodash";
import { createDirtyChecker } from "../../gvc/testNewForm/createDirtyChecker";
import {
  inputToAddons,
  inputToAwsProvider,
  inputToAzureProvider,
  inputToDigitalOceanProvider,
  inputToEphemeralProvider,
  inputToGcpProvider,
  inputToGenericProvider,
  inputToHetznerProvider,
  inputToLambdalabsProvider,
  inputToLinodeProvider,
  inputToOblivusProvider,
  inputToPaperspaceProvider,
  inputToTritonProvider,
} from "./utils/inputToMk8s";

type ProviderConverter = (input: any) => any;

const providerConverters: Record<string, { fn: ProviderConverter; extraPools?: string[] }> = {
  aws: { fn: inputToAwsProvider },
  azure: { fn: inputToAzureProvider },
  digitalocean: { fn: inputToDigitalOceanProvider },
  ephemeral: { fn: inputToEphemeralProvider },
  gcp: { fn: inputToGcpProvider },
  generic: { fn: inputToGenericProvider },
  hetzner: { fn: inputToHetznerProvider, extraPools: ["dedicatedServerNodePools"] },
  lambdalabs: { fn: inputToLambdalabsProvider, extraPools: ["unmanagedNodePools"] },
  linode: { fn: inputToLinodeProvider },
  oblivus: { fn: inputToOblivusProvider, extraPools: ["unmanagedNodePools"] },
  paperspace: { fn: inputToPaperspaceProvider, extraPools: ["unmanagedNodePools"] },
  triton: { fn: inputToTritonProvider },
};

export function getDirtyChecker(id: string) {
  return createDirtyChecker(id, (x) => x, (i, v) => {
    const dirty: Record<string, boolean> = {};

    if (i.description !== v.description) dirty.description = true;

    const provider = v.provider;
    const entry = providerConverters[provider];
    if (entry) {
      const iP = entry.fn(i[provider]);
      const vP = entry.fn(v[provider]);

      // Extract node pools for separate dirty tracking
      const iNodePools = iP.nodePools;
      const vNodePools = vP.nodePools;
      delete iP.nodePools;
      delete vP.nodePools;

      const extraPools: Record<string, { i: any; v: any }> = {};
      for (const poolKey of entry.extraPools || []) {
        extraPools[poolKey] = { i: iP[poolKey], v: vP[poolKey] };
        delete iP[poolKey];
        delete vP[poolKey];
      }

      if (!isEqual(iP, vP)) dirty[`provider-${provider}`] = true;
      const maxNodePools = Math.max((iNodePools || []).length, (vNodePools || []).length);
      for (let j = 0; j < maxNodePools; j++) {
        if (!isEqual(iNodePools?.[j], vNodePools?.[j])) dirty[`${provider}.nodePools.${j}`] = true;
      }
      for (const [poolKey, pools] of Object.entries(extraPools)) {
        const maxPools = Math.max((pools.i || []).length, (pools.v || []).length);
        for (let j = 0; j < maxPools; j++) {
          if (!isEqual(pools.i?.[j], pools.v?.[j])) dirty[`${provider}.${poolKey}.${j}`] = true;
        }
      }

      const advancedInputFields: Record<string, string[]> = {
        aws: ["networking", "autoscaler", "awsTags", "deployRoleChain", "extraNodePolicies", "skipCreateRoles", "preInstallScript"],
        azure: ["networking", "autoscaler", "tags", "preInstallScript"],
        digitalocean: ["networking", "autoscaler", "extraSshKeys", "digitalOceanTags", "reservedIPs", "preInstallScript"],
        gcp: ["networking", "autoscaler", "tags", "labels", "metadata", "preInstallScript"],
        generic: ["networking"],
        hetzner: ["networking", "autoscaler", "floatingIPSelector", "hetznerLabels", "preInstallScript"],
        triton: ["networking", "autoscaler", "preInstallScript", "loadBalancer"],
      };
      const advFields = advancedInputFields[provider] || [];
      if (advFields.some((field) => !isEqual(i[provider]?.[field], v[provider]?.[field]))) {
        dirty[`provider-${provider}-advanced`] = true;
      }
    }

    if (!isEqual(inputToAddons(i), inputToAddons(v))) dirty.addons = true;
    if (!isEqual(i.firewall, v.firewall)) dirty.firewall = true;
    if (!isEqual(i.tags, v.tags)) dirty.tags = true;

    return dirty;
  });
}
